import {
  Component,
  OnInit,
  Input,
  ChangeDetectionStrategy,
  signal,
  inject,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
  FormControl,
} from '@angular/forms';
import { debounceTime, tap, Subject } from 'rxjs';
import { Address } from 'src/app/models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { State } from '../../state/state';
import { CommonModule } from '@angular/common';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { HasRequiredPipe } from '../../pipes/has-required.pipe';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { JoinPipe } from '../../pipes/join.pipe';
import { Utils } from '../../services/utilities';
import { toObservable } from '@angular/core/rxjs-interop';
import { AddressHttpService } from '../../services/http/address-http.service';
@UntilDestroy()
@Component({
  selector: 'app-address-form',
  changeDetection: ChangeDetectionStrategy.Default,
  templateUrl: './address-form.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzFormModule,
    NzSpinModule,
    NzInputModule,
    NzSelectModule,
    NzTagModule,
    HasRequiredPipe,
    NzButtonModule,
    NzIconModule,
    JoinPipe,
  ],
})
export class AddressFormComponent implements OnInit, OnChanges {
  readonly fb = inject(FormBuilder);
  private readonly utils = inject(Utils);
  readonly state = inject(State);
  readonly addressHttp = inject(AddressHttpService);
  @Input() address: Partial<Address>;
  @Input() addressId: number;
  @Input() predefined = [];
  @Input() showHeadquarters = true;
  @Input() multiInputs = true;
  @Input() required = true;
  form = new FormGroup({
    search: new FormControl(null),
    address1: new FormControl(null, Validators.required),
    address2: new FormControl(null),
    postcode: new FormControl(null, Validators.required),
    city: new FormControl(null, Validators.required),
  });
  search$ = new Subject();
  openSearchLocation = signal(false);
  headquarters = [
    {
      id: this.state.headquarters.address_id,
      operation: 'retrieve',
      query: this.state.headquarters.labels,
      headquarters: true,
    },
  ];
  addresses: Partial<Address>[] = [];
  loading = signal(false);

  constructor() {
    this.listenToSearching();
    this.listenToLoading();
  }

  ngOnInit() {
    if (this.address) {
      this.initByAddressObject();
    }

    if (this.addressId) {
      this.initByAddressId();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.required) {
      this.setValidators();
    }
  }

  initByAddressObject(): void {
    const { labels, postcode, address1, address2, city } = this.address;
    this.form.patchValue({
      search: {
        labels,
      },
      address1,
      address2,
      postcode,
      city,
    });
    this.setValidators();
  }

  initByAddressId(): void {
    this.addressHttp.getAddress(this.addressId).subscribe(res => {
      const { postcode, address1, address2, city } = res[0];
      const address = {
        id: this.addressId,
        labels: [postcode, address1, address2, city].filter(Boolean),
      };
      this.addresses.push(address);
      this.updateForm({
        ...res[0],
        id: this.addressId,
        search: address,
      });
      this.setValidators();
    });
  }

  findAddress(option): void {
    this.addressHttp
      .crafty({
        operation: 'find',
        query: option.query || option?.labels,
        id: option.id,
      })
      .subscribe(resp => {
        if (resp.results?.length > 1) {
          this.search$.next(option.query || option?.labels);
        } else {
          this.retrieveAddress(option);
        }
      });
  }

  retrieveAddress(option): void {
    this.addressHttp
      .crafty({
        operation: 'retrieve',
        query: option?.query || option?.labels,
        id: option?.id,
      })
      .subscribe(res => {
        const { postal_code, line_1, line_2, province_name } = res.result;
        this.updateForm({
          id: null, // because is new and has to be created
          address1: line_1,
          address2: line_2,
          postcode: postal_code,
          city: province_name,
        });
      });
  }

  setValidators(): void {
    if (!this.required) {
      this.form.controls['address1'].setValidators([]);
      this.form.controls['postcode'].setValidators([]);
      this.form.controls['city'].setValidators([]);
    } else {
      this.form.controls['address1'].setValidators([Validators.required]);
      this.form.controls['postcode'].setValidators([Validators.required]);
      this.form.controls['city'].setValidators([Validators.required]);
    }
  }

  updateFormValidity(): void {
    for (const i in this.form.controls) {
      this.form.controls[i].updateValueAndValidity();
    }
  }

  openMap(): void {
    window.open(
      `https://maps.google.com/?q=${this.utils.formatAddress(this.form.value)}`,
      '_blank'
    );
  }

  keyDown($event) {
    if ($event.key.toLowerCase() === 'delete') {
      this.updateForm({
        id: null,
        search: null,
        address1: null,
        address2: null,
        postcode: null,
        city: null,
      });
    }
  }

  setAddress(option) {
    if (typeof option?.id === 'number') {
      this.updateForm(option);
    } else if (option) {
      this.openSearchLocation.set(false);
      this.findAddress(option);
    } else {
      this.form.patchValue(this.emptyAddress());
    }
  }

  listenToSearching(): void {
    this.search$
      .pipe(
        tap(() => {
          this.loading.set(true);
        }),
        debounceTime(300)
      )
      .subscribe(res => {
        if (res) {
          this.performSearch(res);
        } else {
          this.addresses = [];
          this.loading.set(false);
        }
      });
  }

  // === Public Methods ===
  public get valid(): boolean {
    if (!this.form) return false;
    if (this.loading()) return false;
    return this.required ? this.form.valid : true;
  }

  public get values(): Partial<
    Record<'address1' | 'address2' | 'postcode' | 'city' | 'id' | 'search', any>
  > {
    return this.form.value;
  }

  public createAddress() {
    return this.addressHttp.createAddress({
      address1: this.values.address1,
      address2: this.values.address2,
      city: this.values.city,
      postcode: this.values.postcode,
    });
  }

  // === Private Methods ===
  private updateForm(
    data: Partial<
      Record<
        'address1' | 'address2' | 'postcode' | 'city' | 'id' | 'search',
        any
      >
    >
  ): void {
    this.form.patchValue(data, { emitEvent: false });
    this.updateFormValidity();
  }

  private listenToLoading(): void {
    toObservable(this.loading)
      .pipe(untilDestroyed(this))
      .subscribe(isLoading => {
        if (!this.form) return;

        const controls = ['address1', 'address2', 'postcode', 'city'];
        controls.forEach(c =>
          this.form.get(c)?.[isLoading ? 'disable' : 'enable']()
        );
      });
  }

  private performSearch(search) {
    this.addressHttp
      .crafty({ operation: 'find', query: search })
      .subscribe(res => {
        this.addresses = res.results;
        if (this.addresses?.length && !this.openSearchLocation()) {
          this.openSearchLocation.set(true);
        }
        this.loading.set(false);
      });
  }

  private emptyAddress() {
    return { address1: null, address2: null, postcode: null, city: null };
  }
}
